import {
  getFlowConfigMeta,
  getFrustrationSignalConfigMeta,
  getPreDefinedMetricsMeta,
  getUserDefinedMetricsMeta,
  getEventMetricsMeta,
} from '@src/api/meta';
import { isAppModule } from '@src/utils/constants';
import { expandPercentileMetrics } from '@src/utils/percentile';
import { trackQueryError } from '@src/utils/sensor/apptracker';
import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import useMetricsStore from '@globalStore/metricsStore';
import useLimitationStore from '@globalStore/limitationStore';
import { getDimensions } from '@api/dimensionContainer';
import useDimensionsStore from '@globalStore/dimensionsStore';
import { useDispatch } from 'react-redux';
import { fetchingSuccess } from '@store/common/dimensionList/actions';
import { initOdp } from '../initOdp';
import presetStore from '@src/globalStore/presetsStore';
import { get } from 'lodash';
import { useAtomValue } from 'jotai';
import { coreFetchedAtom } from '@src/routers/utils';

const useMetricsAndDimensions = () => {
  const context = window.conviva.context;
  const {
    mergeCombinedMetricsMeta,
    setFlowConfigMeta,
    setFlowMetricsMeta,
    setFrustrationSignalMetricsMeta,
    setFrustrationSignalConfigMeta,
    setEventConfigMeta,
  } = useMetricsStore.getState();
  const { setDimensionsMeta } = useDimensionsStore.getState();
  const { setLimitations } = useLimitationStore.getState();
  const { setFlowPresets, setFrustrationSignalPresets, setPresets, setEventPreset } =
    presetStore.getState();
  const dispatch = useDispatch();

  // disable hooks post all fetching tasks to avoid risk of any re-triggers
  const disabled = useAtomValue(coreFetchedAtom);

  // user defined
  const {
    isFetched: isUserDefinedMetricsFetched,
    isError: isUserDefMetricsError,
    data: userDefinedMetrics,
  } = useQuery(
    ['user-defined-metrics-meta'],
    async () => {
      const resp = await getUserDefinedMetricsMeta({ context: context, forceUpdate: true });
      return resp.data;
    },
    {
      retry: 1,
      staleTime: Infinity,
      cacheTime: Infinity,
      refetchOnWindowFocus: false,
      notifyOnChangeProps: ['data', 'isError', 'isFetched'],
      enabled: !disabled,
      onError: (err) => {
        trackQueryError({ type: 'user-defined-metrics-meta', err });
      },
    },
  );

  // pre defined
  const {
    isFetched: isPreDefinedMetricsFetched,
    isError: isPreDefinedMetricsError,
    data: preDefinedMetrics,
  } = useQuery(
    ['pre-defined-metrics-meta'],
    async () => {
      const resp = await getPreDefinedMetricsMeta({ context: context, forceUpdate: true });
      return resp.data;
    },
    {
      retry: 1,
      staleTime: Infinity,
      cacheTime: Infinity,
      refetchOnWindowFocus: false,
      notifyOnChangeProps: ['data', 'isError', 'isFetched'],
      enabled: !disabled,
      onError: (err) => {
        trackQueryError({ type: 'pre-defined-metrics-meta', err });
      },
    },
  );

  // dimensions
  const {
    isFetched: isDimensionListFetched,
    isError: isDimensionListError,
    data: dimensionList,
  } = useQuery(
    ['dimension-list'],
    async () => {
      const resp = await getDimensions({ context: context });
      return resp.data;
    },
    {
      retry: 1,
      staleTime: Infinity,
      refetchOnWindowFocus: false,
      notifyOnChangeProps: ['data', 'isError', 'isFetched'],
      enabled: !disabled,
      onError: (err) => {
        trackQueryError({ type: 'dimension-list', err });
      },
    },
  );

  // flow
  const hasFlow = isAppModule;
  const {
    isFetched: isFlowMetricsFetched,
    isError: isFlowMetricsError,
    data: flowConfigMeta = [],
  } = useQuery(
    ['flow-metrics-meta'],
    async () => {
      const resp = await getFlowConfigMeta({ forceUpdate: true });
      return get(resp, ['data'], {});
    },
    {
      enabled: hasFlow && !disabled,
      retry: 1,
      staleTime: Infinity,
      cacheTime: Infinity,
      refetchOnWindowFocus: false,
      notifyOnChangeProps: ['data', 'isError', 'isFetched'],
    },
  );

  // Frustration Signals
  const hasFrustrationSignal = isAppModule;
  const {
    isFetched: isFrustrationSignalMetricsFetched,
    isError: isFrustrationSignalMetricsError,
    data: frustrationSignalMetricsMeta = [],
  } = useQuery(
    ['frustration-signal-metrics-meta'],
    async () => {
      const resp = await getFrustrationSignalConfigMeta({ forceUpdate: true });
      return get(resp, ['data'], {});
    },
    {
      enabled: hasFrustrationSignal && !disabled,
      retry: 1,
      staleTime: Infinity,
      cacheTime: Infinity,
      refetchOnWindowFocus: false,
      notifyOnChangeProps: ['data', 'isError', 'isFetched'],
    },
  );

  const hasEvent = isAppModule;
  const {
    isFetched: isEventMetricsFetched,
    isError: isEventMetricsError,
    data: eventMetricsMeta = [],
  } = useQuery(
    ['event-metrics-meta'],
    async () => {
      const resp = await getEventMetricsMeta({ forceUpdate: true });
      return get(resp, ['data'], {});
    },
    {
      enabled: hasEvent && !disabled,
      retry: 1,
      staleTime: Infinity,
      cacheTime: Infinity,
      refetchOnWindowFocus: false,
      notifyOnChangeProps: ['data', 'isError', 'isFetched'],
    },
  );

  const isFetched =
    isUserDefinedMetricsFetched &&
    isPreDefinedMetricsFetched &&
    isDimensionListFetched &&
    (!hasFrustrationSignal || isFrustrationSignalMetricsFetched) &&
    (!hasEvent || isEventMetricsFetched) &&
    (!hasFlow || isFlowMetricsFetched);

  useMemo(() => {
    if (isFetched) {
      if (preDefinedMetrics && preDefinedMetrics.app) {
        expandPercentileMetrics(preDefinedMetrics.app);
      }
      mergeCombinedMetricsMeta(preDefinedMetrics, userDefinedMetrics);
      setDimensionsMeta(dimensionList);
      setFlowConfigMeta(flowConfigMeta);
      setFlowMetricsMeta(flowConfigMeta);
      setFrustrationSignalConfigMeta(frustrationSignalMetricsMeta);
      setFrustrationSignalMetricsMeta(frustrationSignalMetricsMeta);
      setEventConfigMeta(eventMetricsMeta);
      setPresets();
      setLimitations();
      setFlowPresets(flowConfigMeta);
      setFrustrationSignalPresets(frustrationSignalMetricsMeta);
      setEventPreset(eventMetricsMeta);
      // TODO: is this required
      dispatch(fetchingSuccess(dimensionList));

      // TODO: should odp/eco have their own init flow
      initOdp({ userDefinedMetrics, dispatch, dimensionList });
      //initEco({ userDefinedMetrics, dispatch, dimensionList });
    }
  }, [isFetched]);

  return {
    isFetched,
  };
};

export default useMetricsAndDimensions;
